import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { IRootReducerState } from "../store/IRootReducer";
import { getInstanceId } from "../utils/authService";
import { DEFAULT_PRODUCT } from "../../shared/products/keys";
import { activeProductFor } from "../components/products/activeProduct";

interface ActivityEvent {
  actionType: string;
  category?: string;
  pagePath?: string;
  pageTitle?: string;
  details?: Record<string, unknown>;
  durationMs?: number;
}

/**
 * Only hire pages are tracked here. A row from the browser carries no product tag (the server
 * reserves that key), so a performance page view would land in the hire activity feed; performance
 * activity is audited on the server instead (Task 11 M4).
 */
function isHirePage(pagePath: string | undefined): boolean {
  if (!pagePath) return true;
  return activeProductFor(pagePath.split(/[?#]/)[0]) === DEFAULT_PRODUCT;
}

const BATCH_INTERVAL = 15000; // 15 seconds — page view events aren't time-critical
const MAX_BATCH_SIZE = 20;

export function useActivityTracking() {
  const location = useLocation();
  const accessToken = useSelector((state: IRootReducerState) => state.authReducer.accessToken);
  const instanceId = getInstanceId();

  const eventQueue = useRef<ActivityEvent[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPageView = useRef<string>("");
  const pageEnterTime = useRef<number>(Date.now());
  const sessionId = useRef<string>(
    sessionStorage.getItem("activity_session_id") || generateSessionId(),
  );

  // Save session ID
  useEffect(() => {
    sessionStorage.setItem("activity_session_id", sessionId.current);
  }, []);

  const flushEvents = useCallback(async () => {
    if (eventQueue.current.length === 0 || !accessToken || !instanceId) return;

    const events = [...eventQueue.current];
    eventQueue.current = [];

    try {
      await fetch("/api/v1/activity-logs/batch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          instanceId,
          events: events.map((e) => ({
            ...e,
            sessionId: sessionId.current,
          })),
        }),
      });
    } catch {
      // Silently fail - don't break the app for analytics
    }
  }, [accessToken, instanceId]);

  const queueEvent = useCallback(
    (event: ActivityEvent) => {
      if (!instanceId) return;
      if (!isHirePage(event.pagePath)) return;

      eventQueue.current.push(event);

      if (eventQueue.current.length >= MAX_BATCH_SIZE) {
        flushEvents();
      } else if (!flushTimer.current) {
        flushTimer.current = setTimeout(() => {
          flushEvents();
          flushTimer.current = null;
        }, BATCH_INTERVAL);
      }
    },
    [flushEvents, instanceId],
  );

  // Track page views on route change
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    if (currentPath === lastPageView.current) return;

    // Log duration on previous page
    if (lastPageView.current) {
      const duration = Date.now() - pageEnterTime.current;
      if (duration > 1000) {
        queueEvent({
          actionType: "page_view",
          category: "navigation",
          pagePath: lastPageView.current,
          pageTitle: document.title,
          durationMs: duration,
          details: { type: "page_exit" },
        });
      }
    }

    lastPageView.current = currentPath;
    pageEnterTime.current = Date.now();

    queueEvent({
      actionType: "page_view",
      category: "navigation",
      pagePath: currentPath,
      pageTitle: document.title,
    });
  }, [location.pathname, location.search, queueEvent]);

  // Flush on unmount / page close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (eventQueue.current.length > 0 && accessToken && instanceId) {
        const events = eventQueue.current.map((e) => ({
          ...e,
          sessionId: sessionId.current,
        }));
        navigator.sendBeacon("/api/v1/activity-logs/batch", JSON.stringify({ instanceId, events }));
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushEvents();
    };
  }, [flushEvents, accessToken, instanceId]);

  const trackAction = useCallback(
    (actionType: string, details?: Record<string, unknown>) => {
      queueEvent({
        actionType,
        category: "interaction",
        pagePath: location.pathname,
        pageTitle: document.title,
        details,
      });
    },
    [queueEvent, location.pathname],
  );

  const trackSearch = useCallback(
    (query: string, resultCount?: number) => {
      queueEvent({
        actionType: "search",
        category: "interaction",
        pagePath: location.pathname,
        details: { query, resultCount },
      });
    },
    [queueEvent, location.pathname],
  );

  return { trackAction, trackSearch };
}

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
